import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Code2, MapPinned, Building, Activity } from 'lucide-react';
import CountUp from '../../components/CountUp';

const howAIDecides = [
  'Strong, consistent presence across directories and citations',
  'Structured data that AI can read and interpret cleanly',
  'A well-optimised Google Business Profile with rich entity data',
  'Authoritative third-party mentions and reviews',
  'Clear, crawlable signals about what the business does and where'
];

const engines = [
  'ChatGPT (including Search and GPT-4o)',
  'Google Gemini (Search Generative Experience)',
  'Perplexity AI',
  'Microsoft Copilot (Bing AI)',
  'Claude (Anthropic)',
  'Apple Intelligence (emerging)'
];

const services = [
  { icon: <Code2 className="w-6 h-6" />, title: 'Schema Markup & Structured Data', desc: 'We implement and maintain schema.org markup for your client\'s business type — LocalBusiness, MedicalBusiness, LegalService, etc. The clearest signal AI engines use to understand and categorise a business.', items: ['Business type schema', 'Service schema', 'FAQ schema', 'Review schema'] },
  { icon: <MapPinned className="w-6 h-6" />, title: 'NAP & Citation Consistency', desc: 'Name, address, and phone number consistency across all major directories is foundational to AI recommendation. We audit, fix, and monitor citations across Google, Yelp, Bing Places, Apple Maps, and 50+ directories globally.', items: ['Citation audit across 50+ directories', 'Inconsistency fixes', 'Ongoing monitoring', 'New citation building in target markets'] },
  { icon: <Building className="w-6 h-6" />, title: 'Google Business Profile Optimisation', desc: 'GBP remains a primary data source for AI recommendation engines. We optimise every element — categories, services, attributes, posts, Q&A — to maximise entity clarity and local relevance.', items: ['Full profile audit and optimisation', 'Category and attribute refinement', 'Weekly post schedule', 'Q&A seeding and management'] },
  { icon: <Activity className="w-6 h-6" />, title: 'AI Citation Monitoring', desc: 'Monthly tracking of how often your client appears in ChatGPT, Gemini, and Perplexity responses for their target queries — position, frequency, and competitor comparison, in plain English under your brand.', items: ['Monthly citation frequency tracking', 'Competitor share of voice comparison', 'Query-level breakdown', 'White-label monthly report'] }
];

const markets = [
  { name: 'North America', items: ['Google, Yelp, BBB, Foursquare', 'Apple Maps, Bing Places', 'Industry directories by vertical', 'US & Canada launch market'] },
  { name: 'United Kingdom', items: ['Google, Trustpilot, Yell.com', 'Checkatrade, FreeIndex', 'Companies House entity signals', 'Full support available now'] },
  { name: 'Australia & NZ', items: ['Google, True Local, Hotfrog', 'Yellow Pages AU, NoCowboys NZ', 'ServiceSeeking, Oneflare', 'Full support available now'] },
  { name: 'Europe & Global', items: ['Market-specific directory mapping', 'Multi-language schema support', 'Local entity platform coverage', 'Ask about your target market'] }
];

const stats = [
  { value: '49%', label: 'Of Google searches now end without a click — users get the answer from AI directly' },
  { value: '3×', label: 'More likely to be recommended if your GBP and schema are fully optimised for AI engines' },
  { value: '0', label: 'Media spend required. GEO is pure organic — your client pays for service, not clicks.' }
];

const altBg = { background: 'rgba(74, 53, 128, 0.07)' };

const RankGeoPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="rank-geo-page">
      <Helmet><title>MarqiRank — Generative Engine Optimisation (GEO)</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="rank-geo-hero-title">
            When Someone Asks ChatGPT <span className="text-[var(--rank-accent)]">'Best [Business] Near Me'</span> — Your Client Needs to Be the Answer.
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            Generative Engine Optimisation (GEO) is the practice of making your client's business appear
            when AI engines — ChatGPT, Gemini, Perplexity, Claude — answer local and category
            recommendation queries. No media spend. No paid placement. Pure organic presence.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="rank-geo-cta-primary">
              Get a Free GEO Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/rank" className="btn-secondary">See How It Works</Link>
          </div>
        </div>
      </section>

      {/* What GEO is */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">AI Recommendation Queries Are the New Word of Mouth.</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="feature-card">
              <h3 className="h3 text-[var(--text-primary)] mb-4">How AI Decides Who to Recommend</h3>
              <p className="body-md mb-6">When someone asks ChatGPT 'best accountant for freelancers in Manchester' — the AI doesn't run a Google search. It draws from training data and real-time retrieval, prioritising businesses that have:</p>
              <ul className="space-y-3 mb-6">
                {howAIDecides.map((h) => (
                  <li key={h} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{h}</span>
                  </li>
                ))}
              </ul>
              <p className="body-md">GEO is the process of systematically building and strengthening all of these signals — so your client is the business the AI recommends.</p>
            </div>
            <div className="space-y-8">
              <div className="feature-card">
                <h3 className="h3 text-[var(--text-primary)] mb-4">What GEO Is Not</h3>
                <p className="body-md">GEO is not traditional SEO — though it complements it. It is not paid advertising. It is not content marketing alone. It is a specific set of technical and structural optimisations that influence how AI engines perceive and recommend a business.</p>
              </div>
              <div className="feature-card">
                <h3 className="h3 text-[var(--text-primary)] mb-4">Which AI Engines We Optimise For</h3>
                <ul className="space-y-3">
                  {engines.map((e) => (
                    <li key={e} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)] text-sm">{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">What a GEO Engagement Includes.</h2>
            <p className="body-lg">Every MarqiRank GEO programme covers the following — delivered under your agency brand, with monthly reporting on progress.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => (
              <div key={s.title} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-[var(--rank-accent)] mr-4" style={{ background: 'rgba(74,53,128,0.18)' }}>{s.icon}</div>
                  <h3 className="h3 text-[var(--text-primary)]">{s.title}</h3>
                </div>
                <p className="body-md mb-6">{s.desc}</p>
                <ul className="space-y-2">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start space-x-3">
                      <Check className="w-4 h-4 text-[var(--rank-accent)] flex-shrink-0 mt-1" />
                      <span className="text-[var(--text-secondary)] text-sm">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">GEO Works in Every Market. We Know the Local Signals.</h2>
            <p className="body-lg">The foundational signals AI engines use are universal — but the directory landscape, local review platforms, and entity data sources vary by market. MarqiRank GEO is built for global delivery.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {markets.map((m, i) => (
              <div key={m.name} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <h3 className="h3 text-[var(--rank-accent)] mb-6">{m.name}</h3>
                <ul className="space-y-3">
                  {m.items.map((it) => (
                    <li key={it} className="flex items-start space-x-3">
                      <Check className="w-4 h-4 text-[var(--rank-accent)] flex-shrink-0 mt-1" />
                      <span className="text-[var(--text-secondary)] text-sm">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats + CTA */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16">The Numbers Behind Why GEO Matters Now.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {stats.map((s, i) => (
              <div key={s.label} className="text-center animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <CountUp value={s.value} className="block text-6xl font-bold text-[var(--rank-accent)] mb-4" />
                <p className="body-md max-w-xs mx-auto">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="feature-card text-center max-w-4xl mx-auto" style={altBg}>
            <h2 className="h2 text-[var(--text-primary)] mb-6">Get Your Client's AI Visibility Baseline — Free.</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">We'll run a complimentary GEO audit for one of your clients — showing exactly how often they appear in AI recommendation answers today, and what it would take to improve it.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Get a Free GEO Audit<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/rank" className="btn-secondary">Back to MarqiRank Overview</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RankGeoPage;
